import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  /** The tab's label, named in the message. */
  label: string;
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * Catches a render error inside ONE reporting tab (tv-zits) so a single bad
 * record shows a message in place of that tab, instead of unmounting the whole
 * app. Hosts key it by tab, so switching tabs starts clean.
 */
export default class ReportingTabBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`Reporting tab "${this.props.label}" failed to render`, error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="panel p-4" data-testid="reporting-tab-error">
        <p className="font-inter text-sm text-cream">The {this.props.label} figures couldn&rsquo;t be shown.</p>
        <p className="font-serif text-xs text-cream-soft mt-1">
          A record in this range has data the report can&rsquo;t read. The other tabs still work. Let the team know
          which tab and date range you were on.
        </p>
      </div>
    );
  }
}
